import { useState } from "react";
import { currencyFormatter } from "@common/helpers/currency";
import { getPSTDate, toIsoDate } from "@common/helpers/date";
import { ScheduleApplicationListMilestone } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import { useRecognizeScheduleMilestoneMutation } from "@api/scheduleMilestone.api";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import { HStack } from "@design-system/HStack/HStack";
import { Switch } from "@design-system/Switch/Switch";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ScheduleOptionsModal } from "../options/ScheduleOptionsModal";
import { useScheduleLockedPeriodsDialog } from "../useScheduleLockedPeriodsDialog";
import {
    formatMilestoneAchievedBulkBody,
    formatMilestoneAchievedBulkTitle,
    formatMilestoneAlreadyRecognizedToast,
    formatMilestoneRecognizedToast,
    formatMilestonesNotDueYetWarning,
    MILESTONE_ACHIEVED_BATCH_DATE_LABEL,
} from "./milestoneAchievedCopy";
import {
    buildDefaultMilestonePostingDates,
    countMilestonesNotDueYet,
    MilestonePostingDates,
    resolveMilestonePostingDates,
} from "./milestoneAchievedDates";
import { resolveMilestoneAchievedFailure } from "./milestoneAchievedFailure";
import {
    MilestoneAchievedBulkRow,
    MilestoneAchievedRowsList,
} from "./MilestoneAchievedRowsList";
import { useMilestoneAchievedBulkSubmit } from "./useMilestoneAchievedBulkSubmit";
import styles from "./MilestoneAchievedModal.module.scss";

export interface MilestoneAchievedTarget {
    applicationId: string;
    entityId: number;
    amount: number;
    milestone: ScheduleApplicationListMilestone;
}

export interface MilestoneAchievedBulkTarget {
    rows: MilestoneAchievedBulkRow[];
    /** Selected rows that aren't pending milestones; reported, not posted. */
    skippedCount: number;
}

type AnyMilestoneAchievedTarget =
    | MilestoneAchievedTarget
    | MilestoneAchievedBulkTarget;

function isBulkTarget(
    target: AnyMilestoneAchievedTarget,
): target is MilestoneAchievedBulkTarget {
    return "rows" in target;
}

/** Both confirms propose each milestone's expected date; the bulk batch date starts at today. */
function getDefaultPostingDate(target: AnyMilestoneAchievedTarget): string {
    return isBulkTarget(target)
        ? toIsoDate(getPSTDate())
        : target.milestone.expectedDate;
}

function getDefaultRowDates(
    target: AnyMilestoneAchievedTarget,
): MilestonePostingDates {
    return isBulkTarget(target)
        ? buildDefaultMilestonePostingDates(target.rows)
        : {};
}

interface MilestoneAchievedModalProps {
    target: AnyMilestoneAchievedTarget | null;
    onClose: () => void;
    /** After a bulk confirm posted at least one row. */
    onCompleted?: () => void;
}

export const MilestoneAchievedModal = ({
    target,
    onClose,
    onCompleted,
}: MilestoneAchievedModalProps) => {
    const { toast, error } = useToaster();
    const [displayedTarget, setDisplayedTarget] =
        useState<AnyMilestoneAchievedTarget | null>(target);
    const [postingDate, setPostingDate] = useState<string>(
        target ? getDefaultPostingDate(target) : "",
    );
    const [rowDates, setRowDates] = useState<MilestonePostingDates>(
        target ? getDefaultRowDates(target) : {},
    );
    const [isBatchDateOn, setIsBatchDateOn] = useState(false);
    const showLockedPeriods = useScheduleLockedPeriodsDialog();
    const mutation = useRecognizeScheduleMilestoneMutation();
    const bulkSubmit = useMilestoneAchievedBulkSubmit({
        onClose,
        onCompleted,
    });

    if (target !== null && target !== displayedTarget) {
        setDisplayedTarget(target);
        setPostingDate(getDefaultPostingDate(target));
        setRowDates(getDefaultRowDates(target));
        setIsBatchDateOn(false);
    }
    const resolvedRowDates = resolveMilestonePostingDates({
        rowDates,
        batchDate: postingDate,
        isBatchDateOn,
    });
    const hasEmptyRowDate = Object.values(resolvedRowDates).some(
        (date) => date === "",
    );
    const notDueYetCount = countMilestonesNotDueYet({
        dates: resolvedRowDates,
        today: toIsoDate(getPSTDate()),
    });

    const handleConfirmSingle = async (single: MilestoneAchievedTarget) => {
        try {
            const result = await mutation.mutateAsync({
                applicationId: single.applicationId,
                sortOrder: single.milestone.sortOrder,
                entityId: single.entityId,
                postingDate,
            });
            toast(
                formatMilestoneRecognizedToast({
                    name: single.milestone.name,
                    amount: result.amount,
                    isOverRecognized: result.isOverRecognized,
                }),
            );
            onClose();
        } catch (mutationError) {
            const failure = resolveMilestoneAchievedFailure({
                error: mutationError,
                showLockedPeriods,
            });
            if (failure.kind === "alreadyRecognized") {
                toast(
                    formatMilestoneAlreadyRecognizedToast(
                        single.milestone.name,
                    ),
                );
            } else if (failure.kind !== "lockedPeriod") {
                error(failure.message);
            }
            if (failure.shouldClose) {
                onClose();
            }
        }
    };

    const handleConfirm = async () => {
        if (!displayedTarget) {
            return;
        }
        if (isBulkTarget(displayedTarget)) {
            if (hasEmptyRowDate) {
                return;
            }
            await bulkSubmit.submit({
                target: displayedTarget,
                postingDates: resolvedRowDates,
            });
            return;
        }
        if (postingDate === "") {
            return;
        }
        await handleConfirmSingle(displayedTarget);
    };

    const postingDateField = (
        <Field label="Posting date" control="button">
            <Field.Control
                render={(controlProps) => (
                    <DatePicker
                        mode="day"
                        variant="panel-input"
                        clearable={false}
                        value={postingDate}
                        onValueChange={(value) => setPostingDate(value ?? "")}
                        fieldControlProps={pickFieldControlAriaProps(
                            controlProps,
                        )}
                    />
                )}
            />
        </Field>
    );

    const buildContent = () => {
        if (!displayedTarget) {
            return null;
        }
        const confirmLabel = "Recognize";
        if (isBulkTarget(displayedTarget)) {
            return {
                confirmLabel,
                isConfirmDisabled: hasEmptyRowDate,
                title: formatMilestoneAchievedBulkTitle(
                    displayedTarget.rows.length,
                ),
                body: formatMilestoneAchievedBulkBody(
                    displayedTarget.skippedCount,
                ),
                options: (
                    <>
                        <Switch
                            checked={isBatchDateOn}
                            onCheckedChange={setIsBatchDateOn}
                            label={MILESTONE_ACHIEVED_BATCH_DATE_LABEL}
                        />
                        {isBatchDateOn && postingDateField}
                        <MilestoneAchievedRowsList
                            rows={displayedTarget.rows}
                            dates={resolvedRowDates}
                            isDisabled={isBatchDateOn}
                            onDateChange={({ rowKey, date }) =>
                                setRowDates((current) => ({
                                    ...current,
                                    [rowKey]: date,
                                }))
                            }
                        />
                        {notDueYetCount > 0 && (
                            <span className={styles.warning}>
                                {formatMilestonesNotDueYetWarning(
                                    notDueYetCount,
                                )}
                            </span>
                        )}
                    </>
                ),
            };
        }
        return {
            confirmLabel,
            isConfirmDisabled: postingDate === "",
            title: `Milestone achieved · ${displayedTarget.milestone.name}`,
            body: "Kick posts the recognition entry for this milestone on the posting date. Kick fixed the amount when you applied the policy.",
            options: (
                <>
                    <HStack gap={8} align="center">
                        <span className={styles.label}>Recognized</span>
                        <span className={styles.amount}>
                            {currencyFormatter.format(displayedTarget.amount)}
                        </span>
                    </HStack>
                    {postingDateField}
                </>
            ),
        };
    };
    const isSubmitting = mutation.isPending || bulkSubmit.isPending;

    return (
        <ScheduleOptionsModal
            show={target !== null}
            content={buildContent()}
            isSubmitting={isSubmitting}
            onConfirm={handleConfirm}
            onHide={() => {
                if (!isSubmitting) {
                    onClose();
                }
            }}
            onExited={() => setDisplayedTarget(null)}
        />
    );
};
